import { useCallback, useEffect, useRef, useState, type Dispatch, type RefObject } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useUserProfile } from "@pontimed/auth-client";
import { m, useIsPresent } from "framer-motion";

import { BillingStep, companyProfileRequest } from "@/components/billing/billing-step";
import { hasPerSeatTiers } from "@/components/pricing/clinic-seat-pricing";
import { Button } from "@/components/ui/button";
import { REVERSE_CHARGE_FACTOR } from "@/lib/pricing/buyer-price";
import { calculatePlanPrice } from "@/lib/pricing/calculate-plan-price";

import type { BillingProfileOption, CheckoutAction, CheckoutState } from "./checkout-types";

/**
 * Checkout billing step — 1:1 port of legacy
 * `src/components/billing/checkout/checkout-step-billing.tsx`. Wraps
 * <BillingStep> with controlled state from the checkout reducer.
 *
 * WHO THE INVOICE GOES TO (2026-10). The choice made here used to stay in the
 * reducer: `selectedProfileId` was the account's default profile whatever the
 * buyer picked, and a company typed into the form was never saved. Continue
 * now turns the visible choice into the profile the API records on the
 * subscription (`billingProfileId`), which every invoice of it — renewals
 * included — is issued to (packages/billing/src/invoice-service.ts):
 *   - a saved company → that profile;
 *   - a new company → saved first;
 *   - personal → the account's personal profile, created on first use. With
 *     none, invoicing fell back to the default profile (a company, when the
 *     account has one) or to the payout company data.
 * A profile created here joins the list, so coming back to this step and
 * continuing again reuses it instead of saving a duplicate. It is never made
 * the account's DEFAULT: subscriptions bought before 2026-10 have no recorded
 * choice and invoice the default profile (else the payout data), so a default
 * minted by a checkout — one later abandoned, even — would re-address all of
 * them. The default stays a Settings decision.
 */

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CheckoutStepBillingProps {
  state: CheckoutState;
  dispatch: Dispatch<CheckoutAction>;
  onContinue: () => void;
  onBack?: () => void;
  /** Set to this step's Continue — the modal's mobile footer goes through it. */
  continueRef?: RefObject<(() => void) | null>;
}

interface SavedProfileResponse {
  profile?: BillingProfileOption;
}

function toProfileOption(p: BillingProfileOption): BillingProfileOption {
  return {
    id: p.id,
    profileName: p.profileName || p.businessName || "",
    businessName: p.businessName,
    vatNumber: p.vatNumber,
    registrationNumber: p.registrationNumber,
    fiscalType: p.fiscalType,
    country: p.country || "RO",
    address: p.address || "",
    isDefault: p.isDefault,
    vatStatus: p.vatStatus ?? null,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckoutStepBilling({
  state,
  dispatch,
  onContinue,
  onBack,
  continueRef,
}: CheckoutStepBillingProps) {
  const { t } = useLingui();
  // The name and country a personal profile is created with.
  const { data: account } = useUserProfile();

  // --- Billing profile loading ---
  const alreadyHasProfiles = state.billingProfiles.length > 0;
  const [profilesLoading, setProfilesLoading] = useState(!alreadyHasProfiles);
  // The list never loaded: a personal profile is not created blind (it could
  // be a duplicate), the invoice keeps the old fallback instead.
  const [profilesUnavailable, setProfilesUnavailable] = useState(false);
  const [billingValid, setBillingValid] = useState(true);
  const [attempted, setAttempted] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // False once this step leaves (Back, the stepper, the modal closing): a
  // save that resolves after that must not move the modal forward.
  const isPresent = useIsPresent();
  const presentRef = useRef(true);
  useEffect(() => {
    presentRef.current = isPresent;
    return () => {
      presentRef.current = false;
    };
  }, [isPresent]);

  useEffect(() => {
    if (alreadyHasProfiles) return;
    let cancelled = false;
    apiFetch
      .get<{ profiles?: BillingProfileOption[] }>("/api/account/billing/profile")
      .then((data) => {
        if (cancelled) return;
        const profiles = (data.profiles || []).map(toProfileOption);
        dispatch({ type: "SET_BILLING_PROFILES", profiles });
        // Open on whoever the invoices go to today: the default profile, else
        // the newest (the list comes newest first) — a company bought for in
        // an earlier checkout is never the default but stays chosen when the
        // buyer just continues.
        const current = profiles.find((p) => p.isDefault) ?? profiles[0];
        if (current && current.fiscalType !== "individual") {
          dispatch({ type: "SET_BILLING_CHOICE", choice: "business" });
          dispatch({ type: "SET_BILLING_COMPANY_ID", businessId: current.id });
        }
      })
      .catch(() => {
        if (!cancelled) setProfilesUnavailable(true);
      })
      .finally(() => {
        if (!cancelled) setProfilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = useCallback(
    async (body: Record<string, unknown>): Promise<BillingProfileOption> => {
      const data = await apiFetch.post<SavedProfileResponse>("/api/account/billing/profile", body);
      if (!data.profile?.id) throw new Error("billing profile not returned");
      const saved = toProfileOption(data.profile);
      dispatch({ type: "SET_BILLING_PROFILES", profiles: [...state.billingProfiles, saved] });
      return saved;
    },
    [state.billingProfiles, dispatch],
  );

  /** The profile id the purchase records — see WHO THE INVOICE GOES TO. */
  const resolveInvoiceProfile = useCallback(async (): Promise<string | null> => {
    const profiles = state.billingProfiles;
    if (state.billingChoice === "individual") {
      const personal = profiles.filter((p) => p.fiscalType === "individual");
      const existing = personal.find((p) => p.isDefault) ?? personal[0];
      if (existing) return existing.id;
      if (profilesUnavailable) return null;
      // Only the profile's label in Settings — the invoice itself is issued to
      // the account's own name.
      const label =
        [account?.firstName, account?.lastName].filter(Boolean).join(" ").trim() ||
        account?.email ||
        t`Personal (individual)`;
      const saved = await saveProfile({
        profileName: label,
        fiscalType: "individual",
        country: account?.primaryCountry || "RO",
        isDefault: false,
      });
      return saved.id;
    }
    const companies = profiles.filter((p) => p.fiscalType !== "individual");
    const typingNew = state.billingShowNewBusiness || companies.length === 0;
    if (!typingNew && state.billingSelectedBusinessId) return state.billingSelectedBusinessId;
    const saved = await saveProfile(companyProfileRequest(state.billingForm, { isDefault: false }));
    dispatch({ type: "SET_BILLING_SHOW_NEW", show: false });
    dispatch({ type: "SET_BILLING_COMPANY_ID", businessId: saved.id });
    return saved.id;
  }, [
    state.billingProfiles,
    state.billingChoice,
    state.billingShowNewBusiness,
    state.billingSelectedBusinessId,
    state.billingForm,
    account,
    profilesUnavailable,
    saveProfile,
    dispatch,
    t,
  ]);

  const handleContinue = useCallback(async () => {
    // Nothing to record before the list is in (the mobile footer is not
    // disabled meanwhile).
    if (profilesLoading) return;
    if (!billingValid) {
      setAttempted(true);
      return;
    }
    if (state.loading) return;
    setSaveError(null);
    dispatch({ type: "SET_LOADING", loading: true });
    try {
      const profileId = await resolveInvoiceProfile();
      dispatch({ type: "SET_SELECTED_PROFILE", profileId });
      if (presentRef.current) onContinue();
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setSaveError(body?.error || t`Could not save profile. Please try again.`);
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  }, [
    billingValid,
    profilesLoading,
    state.loading,
    resolveInvoiceProfile,
    onContinue,
    dispatch,
    t,
  ]);

  // The modal's mobile footer cannot see this step's validity or choice: it
  // continues through the same handler.
  useEffect(() => {
    if (!continueRef) return;
    continueRef.current = () => void handleContinue();
    return () => {
      continueRef.current = null;
    };
  }, [continueRef, handleContinue]);

  // Price for invoice preview — mirrors CheckoutModal.useMemo(price) so packs
  // the user selected in the Configure step still show up in this summary.
  // Priced twice: the list price, and what a reverse-charge company pays (each
  // unit net, as the server prices it) — <BillingStep> shows the one its VAT
  // rule picks for the choice on screen.
  const priceAt = (priceFactor: number) =>
    state.selectedPlanData
      ? calculatePlanPrice({
          basePriceMonthly: state.selectedPlanData.priceMonthly,
          basePriceAnnual:
            state.selectedPlanData.priceAnnual > 0 ? state.selectedPlanData.priceAnnual : undefined,
          quantity: state.quantity,
          interval: state.interval,
          volumeTiers: state.selectedPlanData.volumeTiers,
          providersIncluded: state.selectedPlanData.providersIncluded ?? 1,
          // Same flat/graduated decision as CheckoutModal — this preview feeds the
          // VAT/invoice lines and must show the amount that will be charged.
          seatMode: hasPerSeatTiers(state.selectedPlanData) ? "flat" : "graduated",
          packs: state.availablePacks.map((opt) => ({
            featureKey: opt.featureKey,
            batchSize: opt.batchSize,
            batchPriceEur: opt.batchPriceEur,
            count: state.addons.packs[opt.featureKey] ?? 0,
          })),
          priceFactor,
        })
      : null;
  // What Stripe charges now: the whole interval (annual = the year), the same
  // `totalNow` CheckoutSummary shows.
  const chargedNow = (priceFactor: number) => {
    const p = priceAt(priceFactor);
    if (!p) return undefined;
    return state.interval === "annual" ? p.intervalTotal : p.monthlyTotal;
  };

  return (
    <m.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
    >
      <div className="border-b px-5 py-3">
        <h3 className="text-foreground text-sm font-semibold">
          <Trans>Invoice details</Trans>
        </h3>
      </div>

      <BillingStep
        profiles={state.billingProfiles}
        profilesLoading={profilesLoading}
        selectedProfileId={state.selectedProfileId}
        onSelectProfile={(id) => dispatch({ type: "SET_SELECTED_PROFILE", profileId: id })}
        onProceed={() => {}}
        onBack={() => {}}
        loading={state.loading}
        price={chargedNow(1)}
        netPrice={chargedNow(REVERSE_CHARGE_FACTOR)}
        hideFooter
        onValidityChange={setBillingValid}
        controlledChoice={state.billingChoice}
        onChoiceChange={(choice) => dispatch({ type: "SET_BILLING_CHOICE", choice })}
        controlledBusinessId={state.billingSelectedBusinessId}
        onBusinessIdChange={(id) => dispatch({ type: "SET_BILLING_COMPANY_ID", businessId: id })}
        controlledShowNew={state.billingShowNewBusiness}
        onShowNewChange={(show) => dispatch({ type: "SET_BILLING_SHOW_NEW", show })}
        controlledForm={state.billingForm}
        onFormChange={(form) => dispatch({ type: "SET_BILLING_FORM", form })}
        showErrors={attempted}
      />

      {/* Validation error */}
      {attempted && !billingValid && (
        <m.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-destructive/10 text-destructive mx-5 mt-3 rounded-lg p-3 text-sm"
        >
          <Trans>Please fill in all required billing fields.</Trans>
        </m.div>
      )}

      {saveError && (
        <div
          role="alert"
          className="bg-destructive-soft text-destructive-strong mx-5 mt-3 rounded-lg p-3 text-sm"
        >
          {saveError}
        </div>
      )}

      {/* Footer navigation — desktop only */}
      <div className="hidden gap-3 px-5 pt-4 pb-5 md:flex">
        {onBack && (
          <Button
            variant="outline"
            size="lg"
            onClick={onBack}
            disabled={state.loading}
            className="shrink-0"
          >
            <Trans>Back</Trans>
          </Button>
        )}
        <Button
          className="flex-1"
          size="lg"
          onClick={() => void handleContinue()}
          disabled={profilesLoading || state.loading}
        >
          <Trans>Continue</Trans>
        </Button>
      </div>
    </m.div>
  );
}
